import { isLocalStorageSupported } from "./util";

//https://github.com/you-dont-need/You-Dont-Need-Lodash-Underscore?tab=readme-ov-file#_clamp
// Native
const clamp = (number, boundOne, boundTwo) => {
    if (!boundTwo) {
        return Math.max(number, boundOne) === boundOne ? number : boundOne;
    } else if (Math.min(number, boundOne) === number) {
        return boundOne;
    } else if (Math.max(number, boundTwo) === number) {
        return boundTwo;
    }
    return number;
};

const $ = global.$;

const TTL = 60 * 24 * 60 * 60 * 1000; // set expiry to aprox 2 months (60 days) in millisecs
const CLEAN_EXPIRY_INTERVAL = clamp(TTL/2, 0, Math.pow(2, 30)); // clean local interval in minutes (half ttl by default)... not really needed when ttl is months...
const FIRST_CLEAN_DELAY = 60 * 1000; // set first clean delay to 1 min in millisecs


/**
 * Simple wrapper around local storage to allow for data to expire.
 * Expiry date is updated on access (i.e. if data is accessed often, it will never expire)
 *
 * If localstorage is not supported, doesn't do expire and fallback to cookies (using jquery $.localStorage)
 */
export default class LocalStorage {
    private readonly isLocalStorageSupported: boolean;

    constructor() {
        this.isLocalStorageSupported = isLocalStorageSupported();

        // no need to try to clean if localStorage is not supported
        if (this.isLocalStorageSupported) {
            setInterval(() => this.removeExpired(), CLEAN_EXPIRY_INTERVAL);
            setTimeout(() => this.removeExpired(), FIRST_CLEAN_DELAY);
        }
    }
    
    /**
     * private function that gets the required value from the local storage.
     * If updateExpiryDate is true, it re-inserts the entry in localstorage with a new expiry.
     * 
     * @param key 
     * @param updateExpiryDate 
     * 
     * @returns 
     */
    private getRemovingExpired(key: string, updateExpiryDate: boolean): any {
        const itemStr = localStorage.getItem(key);
        if (!itemStr) {
            return null;
        }
        
        // not created by us
        if (!itemStr.startsWith('{"_lsc_expiry":')) {
            if (updateExpiryDate) { // if updateExpiryDate (don't do it when false as it is looping through all to clean, could be storage from some other lib)
                this.store(key, itemStr); // convert it to have expire date
            }
            
            return itemStr;
        }
        
        const item = JSON.parse(itemStr);
        
        if (item._lsc_expiry < Date.now()) {
            localStorage.removeItem(key);
            return null;
        }
        
        const value = item.value;
        
        if (updateExpiryDate) {
            this.store(key, value);
        }
        
        return value;
    }
    
    /**
     * private function that stores in local storage the specified value under the specified key. Expiry information is added to the entry.
     */
    private store(key: string, value: string) {
        // build the JSON String manually to ensure it starts with '{"_lsc_expiry":'
        const itemStr = '{' +
            '"_lsc_expiry":' + (Date.now() + TTL) + ',' +
            '"value":"' + value.replaceAll('"', '\\"') + '"' +
        '}';
        
        localStorage.setItem(key, itemStr);
    }
    
    /**
     * Gets String value from local storage. null if not available (or expired)
     * 
     * @param key 
     * 
     * @returns 
     */
    public get(key: string): string {
        if (typeof key != 'string') {
            throw new Error('Only string key supported by local storage');
        }
        
        return this.isLocalStorageSupported ? this.getRemovingExpired(key, true) : this.parseNonStringValue($.localStorage.getItem(key));
    }
    
    /**
     * Sets String value in local storage
     */
    public set(key: string, value: any) {
        if (typeof key != 'string') {
            throw new Error('Only string key supported by local storage');
        }
        
        if (typeof value == 'number' || typeof value == 'boolean') { // support numeric and boolean values (as we sometimes pass them on without converting)
            value = '' + value;
        }

        if (value === null) {
            value = "_null_";
        }
        
        if (typeof value != 'string') {
            throw new Error('Only string value supported by local storage');
        }
        
        this.isLocalStorageSupported ? this.store(key, value) : $.localStorage.setItem(key, value);
    }
    
    /**
     * Checks if there's a String value in local storage
     * 
     * @param key 
     * 
     * @returns 
     */
    public has(key: string): boolean {
        if (typeof key != 'string') {
            throw new Error('Only string key supported by local storage');
        }
        
        return this.isLocalStorageSupported ? localStorage.hasOwnProperty(key) : $.localStorage.getItem(key) != null;
    }
    
    /**
     * Removes String value from local storage
     * 
     * @param key 
     */
    public remove(key: string) {
        if (typeof key != 'string') {
            throw new Error('Only string key supported by local storage');
        }
        
        this.isLocalStorageSupported ? localStorage.removeItem(key) : $.localStorage.removeItem(key);
    }
    
    /**
     * Removes immediatly all expired entries.
     */
    private removeExpired() {
        if (!this.isLocalStorageSupported) {
            return;
        }
        
        Object.keys(localStorage).forEach(k => this.getRemovingExpired(k, false));
    }

    /**
     * 
     * @param value 
     * 
     * @returns 
     */
    private parseNonStringValue(value: any) {
        if (value === "_null_") {
            return null;
        }

        if (value === "_undefined_") {
            return undefined;
        }

        return value;
    }
}
